import type { MunicipioCandidato } from "@/api/contrato";
import { camposRecusados, FalhaDaApi, textoDoErro, type CampoRecusado } from "@/api/problema";

/*
 * O texto das recusas de cadastro, em vocabulário de tela.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A TRADUÇÃO É POR `codigo`, E NUNCA INTERPRETA `detail` (design D3).      │
 * │                                                                          │
 * │ `detail` vale para `/v1` e para o log, e é contrato de quem integra: ele │
 * │ diz "beneficiário", cita `convenios_permitidos` e até o caminho da rota. │
 * │ O painel não reescreve a API por causa de um rótulo, nem procura pedaço  │
 * │ de frase, que é contrato nenhum. Código que não está aqui segue pelo     │
 * │ `textoDoErro`, porque o `detail` dele já serve à tela.                   │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Sem caminho de rota e sem nome de coluna, inclusive no texto novo: quem lê
 * opera a tela, e não a API. O teste de `recusas.test.ts` confere.
 */
const TEXTO_POR_CODIGO: Readonly<Record<string, string>> = {
  // Vínculo: o `detail` cita rota e `convenios_permitidos`.
  vinculo_anterior_irrestrito:
    "Outra aplicação deste cedente alcança todos os convênios. Restrinja os convênios dela antes.",
  convenios_permitidos_obrigatorio:
    "Este cedente tem outra aplicação. Marque os convênios permitidos nesta.",
  convenio_de_outro_beneficiario: "Um dos convênios marcados é de outro cedente.",
  vinculo_existente: "Esta aplicação já está vinculada a este cedente.",
  modalidade_sem_contrato_de_cedente:
    "Esta aplicação é cobrada pela aplicação. Mude a cobrança para por cedente antes.",

  // Janela de alteração: as duas saídas são opostas, e a frase diz qual.
  alteracao_bancaria_fechada:
    "Já há boleto com estes dados bancários. A correção é feita no banco e com a TecnoSpeed.",
  emissao_em_andamento: "Há boleto a caminho do banco. Repita quando a fila esvaziar.",

  // Município: o `detail` manda informar `codigo_ibge`, que aqui é uma seleção.
  municipio_nao_resolvido: "Município não encontrado nesta UF. Escolha um da lista.",
  municipio_nao_encontrado: "Município não encontrado nesta UF.",
  municipio_inexistente: "Município inexistente.",
  codigo_ibge_de_outra_uf: "O município escolhido é de outra UF.",
  codigo_ibge_diverge_da_cidade: "O município escolhido não é o da cidade informada.",
  uf_invalida: "UF inválida.",

  // O `detail` repete a palavra documento e traz a frase crua do domínio.
  documento_invalido: "CNPJ ou CPF inválido. Confira os dígitos.",

  // Só chegam à tela por defeito do próprio painel, e o `detail` cita o campo.
  campo_proibido: "Este campo não pode ser alterado.",
  identificador_invalido: "Identificador inválido.",

  // O certificado e o provisionamento. O `detail` diz provider, e cita o `api_id`.
  certificado_invalido: "Certificado inválido. Confira o arquivo e a senha.",
  certificado_recusado: "A TecnoSpeed recusou o certificado.",
  certificado_nao_suportado: "O provider deste convênio não usa certificado.",
  convenio_nao_provisionado: "Este convênio não está na TecnoSpeed. Provisione antes.",
  cedente_nao_provisionado: "Este cedente não está na TecnoSpeed. Provisione um convênio antes.",
  cadastro_incompleto: "O cadastro não pode ir à TecnoSpeed como está.",
  credencial_obrigatoria: "Criar o convênio na TecnoSpeed exige a credencial do banco.",
  confirmacao_obrigatoria: "Confirme para provisionar.",
  provisionamento_recusado: "A TecnoSpeed recusou o cadastro.",
  provisionamento_nao_suportado: "O provider deste convênio não provisiona por aqui.",
  // A marca dura 40 s, e sobra depois de uma recusa: "em andamento" nem sempre é verdade.
  provisionamento_em_andamento: "Provisionamento tentado há pouco. Aguarde alguns segundos e repita.",
  provisionamento_incompleto: "O provisionamento parou antes do fim, e repetir completa o que falta.",
  dado_divergente_no_provider: "A TecnoSpeed tem este cadastro com outro dado. A correção lá é manual.",
  tipo_de_conteudo_invalido: "A API não aceitou o formato do envio. Recarregue o painel.",
  payload_too_large: "O envio passa do tamanho aceito.",
  provider_indisponivel: "A TecnoSpeed não respondeu.",
  provider_resposta_invalida: "A resposta da TecnoSpeed não pôde ser lida.",
  provider_sem_orcamento: "Limite de chamadas à TecnoSpeed atingido. Nada foi enviado.",
  nao_configurado: "Este ambiente não tem a integração com a TecnoSpeed.",

  // Só no motivo gravado: pela API, a credencial recusada é `provider_indisponivel`.
  credencial_recusada: "A TecnoSpeed recusou a credencial deste ambiente. Repetir não resolve.",
};

/*
 * Quando o mesmo código aponta campos com saídas diferentes, a frase é do par.
 * `valor_invalido` só tem os campos da credencial: nas outras rotas o `detail` já serve.
 */
const TEXTO_POR_CAMPO: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  certificado_invalido: {
    arquivo: "O arquivo não é um certificado .pfx ou .p12.",
    senha: "A senha tem de 1 a 20 caracteres.",
    apelido: "O apelido tem até 250 caracteres.",
    email: "O e-mail tem até 100 caracteres.",
  },
  certificado_recusado: {
    arquivo: "A TecnoSpeed recusou o arquivo.",
    senha: "A TecnoSpeed recusou a senha.",
    apelido: "A TecnoSpeed recusou o apelido.",
    email: "A TecnoSpeed recusou o e-mail do cedente.",
  },
  credencial_obrigatoria: {
    api_id: "Informe o API - ID: o convênio não existe na TecnoSpeed.",
    api_key: "Informe o API - Key: o convênio não existe na TecnoSpeed.",
    api_secret: "Informe o API - Secret: o convênio não existe na TecnoSpeed.",
    estacao: "Informe a estação: o convênio não existe na TecnoSpeed.",
  },
  provisionamento_recusado: {
    api_id: "A TecnoSpeed recusou o API - ID.",
    api_key: "A TecnoSpeed recusou o API - Key.",
    api_secret: "A TecnoSpeed recusou o API - Secret.",
    estacao: "A TecnoSpeed recusou a estação.",
  },
  valor_invalido: {
    api_id: "O API - ID tem de 1 a 255 caracteres.",
    api_key: "Este banco não usa API - Key.",
    api_secret: "Este banco não usa API - Secret.",
    estacao: "Este banco não usa estação.",
  },
};

/** A frase da recusa pelo par, ou pelo código. `undefined` deixa o `detail` passar. */
function textoPorCodigo(codigo: string, campo: string | undefined): string | undefined {
  const doPar = campo === undefined ? undefined : TEXTO_POR_CAMPO[codigo]?.[campo];
  return doPar ?? TEXTO_POR_CODIGO[codigo];
}

/** As recusas em que `Retry-After` diz quando repetir. */
const REPETE_COM_ESPERA = new Set([
  "provider_indisponivel",
  "provider_sem_orcamento",
  "provisionamento_incompleto",
]);

function comEspera(texto: string, erro: FalhaDaApi): string {
  const repete = erro.codigo !== null && REPETE_COM_ESPERA.has(erro.codigo);
  return repete && erro.tenteEmSegundos !== null
    ? `${texto} Repita em ${erro.tenteEmSegundos} s.`
    : texto;
}

/** O limite no meio do provisionamento: a chamada recusada não saiu, e as de antes já estão lá. */
export function textoDoLimiteNoProvisionamento(erro: FalhaDaApi): string {
  return comEspera("Limite de chamadas à TecnoSpeed atingido.", erro);
}

/** As recusas que juntam os defeitos no `detail`, com o nome da coluna. */
const RECUSA_COM_DEFEITOS = new Set(["dado_do_banco_invalido", "cadastro_incompleto"]);

/** O rótulo de tela de um campo da API, para a recusa que o formulário não mostra. */
const ROTULO_DO_CAMPO: Readonly<Record<string, string>> = {
  banco: "Banco",
  agencia: "Agência",
  agencia_dv: "DV da agência",
  conta: "Conta",
  conta_dv: "DV da conta",
  tipo: "Tipo",
  codigo_beneficiario: "Código do beneficiário",
  banco_correspondente: "Banco correspondente",
  numero: "Número",
  carteira: "Carteira",
  variacao: "Variação",
  padrao_cnab: "Padrão CNAB",
  tipo_webservice: "Tipo de WebService",
  numero_remessa: "Número da remessa",
  numero_contrato: "Número do contrato",
  byte_nosso_numero: "Byte",
  descricao: "Descrição",
  reiniciar_remessa_diariamente: "Reiniciar remessa diariamente",
  registro_instantaneo: "Registro instantâneo",
  documento: "CNPJ ou CPF",
  razao_social: "Razão social",
  nome_fantasia: "Nome fantasia",
  telefone: "Telefone",
  email: "E-mail",
  logradouro: "Logradouro",
  complemento: "Complemento",
  bairro: "Bairro",
  cep: "CEP",
  codigo_ibge: "Município",
  "endereco.logradouro": "Logradouro",
  "endereco.numero": "Número",
  "endereco.complemento": "Complemento",
  "endereco.bairro": "Bairro",
  "endereco.cidade": "Cidade",
  "endereco.uf": "UF",
  "endereco.cep": "CEP",
  "endereco.codigo_ibge": "Município",
  arquivo: "Arquivo",
  senha: "Senha",
  apelido: "Apelido",
  api_id: "API - ID",
  api_key: "API - Key",
  api_secret: "API - Secret",
  estacao: "Estação",
};

/** O rótulo de tela de um campo da API. Nome que a tela não conhece perde o sublinhado. */
export function rotuloDoCampo(campo: string): string {
  return ROTULO_DO_CAMPO[campo] ?? campo.replaceAll("_", " ");
}

/** O domínio escreve defeito como pedaço de frase: a tela o fecha. */
function frase(texto: string): string {
  const aparado = texto.trim();
  if (aparado === "") return aparado;
  const comMaiuscula = aparado.charAt(0).toUpperCase() + aparado.slice(1);
  return /[.!?]$/.test(comMaiuscula) ? comMaiuscula : `${comMaiuscula}.`;
}

function textoDoDefeito(recusado: CampoRecusado): string {
  return textoPorCodigo(recusado.codigo, recusado.campo) ?? frase(recusado.mensagem);
}

/**
 * Fora do campo a frase perde o contexto: "O campo aceita somente dígitos." só
 * diz alguma coisa com o nome de quem foi recusado.
 */
function textoForaDoCampo(recusado: CampoRecusado): string {
  const doPar = TEXTO_POR_CAMPO[recusado.codigo]?.[recusado.campo];
  if (doPar !== undefined) return doPar;

  const rotulo = ROTULO_DO_CAMPO[recusado.campo];
  const texto = textoDoDefeito(recusado);
  return rotulo === undefined ? texto : `${rotulo}: ${texto}`;
}

/** O texto de uma recusa, para a faixa do modal, a confirmação ou o aviso. */
export function textoDaRecusa(erro: unknown): string {
  if (erro instanceof FalhaDaApi && erro.codigo !== null) {
    /*
     * A recusa do catálogo junta os defeitos no `detail` com o nome da coluna:
     * `codigo_beneficiario: ...; agencia_dv: ...`. Os mesmos defeitos vêm um a um.
     */
    const defeitos = camposRecusados(erro);
    if (RECUSA_COM_DEFEITOS.has(erro.codigo) && defeitos.length > 0) {
      return defeitos.map(textoForaDoCampo).join(" ");
    }

    // Indisponível sem `Retry-After` é a credencial do motor recusada: repetir não resolve.
    if (erro.codigo === "provider_indisponivel" && erro.tenteEmSegundos === null) {
      return textoPorCodigo("credencial_recusada", undefined) ?? textoDoErro(erro);
    }

    const traduzido = textoPorCodigo(erro.codigo, erro.problema?.campo);
    if (traduzido !== undefined) return comEspera(traduzido, erro);
  }
  return textoDoErro(erro);
}

/**
 * O motivo gravado na última tentativa, para a `Dica`. Código novo não vira texto cru: o
 * motivo pode ser de meses atrás, e a tela diz só que falhou.
 */
export function textoDoMotivo(motivo: string | null, campo: string | null): string {
  const texto = motivo === null ? undefined : textoPorCodigo(motivo, campo ?? undefined);
  const base = texto ?? "A última tentativa falhou.";
  return campo === null || TEXTO_POR_CAMPO[motivo ?? ""]?.[campo] !== undefined
    ? base
    : `${base} Campo: ${rotuloDoCampo(campo)}.`;
}

export interface ErrosDoFormulario<C extends string> {
  /** O erro de cada campo que o formulário mostra, para `erro` de `Campo` e `Selecao`. */
  porCampo: Partial<Record<C, string>>;
  /** Para `erro` do `ModalDeCadastro`. `undefined` quando tudo achou um campo. */
  geral: string | undefined;
}

/**
 * Reparte uma recusa entre os campos do formulário e a faixa acima dos botões.
 *
 * `campos` são os nomes da API que o formulário mostra AGORA, inclusive os
 * aninhados (`endereco.cep`). Defeito de campo que não está na lista vai para
 * `geral`, com o rótulo: o formulário monta os campos pelo catálogo, e a recusa de
 * um campo escondido não pode sumir, ou o operador fica diante de um botão que não
 * passa e de uma tela sem erro.
 *
 * Sem falha, os dois vêm vazios: o resultado pode ser usado direto no render.
 */
export function errosDoFormulario<C extends string>(
  erro: unknown,
  campos: readonly C[],
): ErrosDoFormulario<C> {
  const porCampo: Partial<Record<C, string>> = {};
  if (erro === null || erro === undefined) return { porCampo, geral: undefined };

  const recusados = camposRecusados(erro);
  if (recusados.length === 0) return { porCampo, geral: textoDaRecusa(erro) };

  const doFormulario = new Set<string>(campos);
  const sobra: CampoRecusado[] = [];
  for (const recusado of recusados) {
    const campo = recusado.campo as C;
    if (doFormulario.has(recusado.campo) && porCampo[campo] === undefined) {
      porCampo[campo] = textoDoDefeito(recusado);
    } else {
      sobra.push(recusado);
    }
  }

  return {
    porCampo,
    geral: sobra.length > 0 ? sobra.map(textoForaDoCampo).join(" ") : undefined,
  };
}

/**
 * Os municípios da recusa `municipio_nao_resolvido`.
 *
 * O formulário os oferece numa seleção, e o escolhido volta como `codigo_ibge`.
 * Vazio não é erro: o nome pode não parecer com município nenhum da UF.
 */
export function candidatosDaRecusa(erro: unknown): readonly MunicipioCandidato[] {
  return erro instanceof FalhaDaApi ? (erro.problema?.candidatos ?? []) : [];
}
